import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetEmspUsersInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users-invoices/get-emsp-users-invoices-paginated.model';
import { EmspUserInvoiceBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/emsp-user-invoice-base.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { EmspBoInvoiceStatusFilterStateController } from '../emsp-bo-invoice-status-filter-state/emsp-bo-invoice-status-filter-state.controller';
import { EmspBoInvoiceTypeFilterStateController } from '../emsp-bo-invoice-type-filter-state/emsp-bo-invoice-type-filter-state.controller';
import { EmspBoInvoiceListEmspUserDidColumn } from './emsp-bo-invoice-list-emsp-user-did.column';
import { EmspBoInvoiceListEmspUserEmailColumn } from './emsp-bo-invoice-list-emsp-user-email.column';
import { EmspBoInvoiceListEmspUserFirstNameColumn } from './emsp-bo-invoice-list-emsp-user-first-name.column';
import { EmspBoInvoiceListEmspUserIdColumn } from './emsp-bo-invoice-list-emsp-user-id.column';
import { EmspBoInvoiceListEmspUserLastNameColumn } from './emsp-bo-invoice-list-emsp-user-last-name.column';
import { EmspBoInvoiceListInvoiceNumberColumn } from './emsp-bo-invoice-list-invoice-number.column';
import { EmspBoInvoiceListIssuedAtColumn } from './emsp-bo-invoice-list-issued-at.column';
import { EmspBoInvoiceListStatusColumn } from './emsp-bo-invoice-list-status.column';
import { EmspBoInvoiceListTotalTtcColumn } from './emsp-bo-invoice-list-total-ttc.column';
import { EmspBoInvoiceListTypeColumn } from './emsp-bo-invoice-list-type.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const EMSP_BO_INVOICE_LIST_SORT_FIELD: NonNullable<
  GetEmspUsersInvoicesPaginatedModel['sortField']
> = 'createdAt';

export const EMSP_BO_INVOICE_LIST_SORT_ORDER: NonNullable<
  GetEmspUsersInvoicesPaginatedModel['sortOrder']
> = -1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetEmspUsersInvoicesPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const EMSP_BO_INVOICE_LIST_FILTER: ResourceListFilter = {
  status: {
    componentType: EmspBoInvoiceStatusFilterStateController,
  },
  type: {
    componentType: EmspBoInvoiceTypeFilterStateController,
  },
} as const;

export const EMSP_BO_INVOICE_LIST_FILTER_NAME_LIST = Object.keys(
  EMSP_BO_INVOICE_LIST_FILTER,
) as Array<keyof typeof EMSP_BO_INVOICE_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const emspBoInvoiceListColumnHelper =
  createColumnHelper<EmspUserInvoiceBaseModel>();

type ResourceColumnType = ColumnDef<EmspUserInvoiceBaseModel, any>[];

export const EMSP_BO_INVOICE_LIST_COLUMN_LIST: ResourceColumnType = [
  emspBoInvoiceListColumnHelper.accessor(
    'invoiceNumber',
    EmspBoInvoiceListInvoiceNumberColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'emspUser.id',
    EmspBoInvoiceListEmspUserIdColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'emspUser.did',
    EmspBoInvoiceListEmspUserDidColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'emspUser.firstName',
    EmspBoInvoiceListEmspUserFirstNameColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'emspUser.lastName',
    EmspBoInvoiceListEmspUserLastNameColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'emspUser.email',
    EmspBoInvoiceListEmspUserEmailColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor('type', EmspBoInvoiceListTypeColumn),
  emspBoInvoiceListColumnHelper.accessor(
    'status',
    EmspBoInvoiceListStatusColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'totalTTC',
    EmspBoInvoiceListTotalTtcColumn,
  ),
  emspBoInvoiceListColumnHelper.accessor(
    'issuedAt',
    EmspBoInvoiceListIssuedAtColumn,
  ),
];

export const EMSP_BO_INVOICE_LIST_COLUMN_ID_LIST =
  EMSP_BO_INVOICE_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const EMSP_BO_INVOICE_LIST_COLUMN_ORDER = [
  ...EMSP_BO_INVOICE_LIST_COLUMN_ID_LIST,
];

export const EMSP_BO_INVOICE_LIST_COLUMN_VISIBILITY: VisibilityState =
  EMSP_BO_INVOICE_LIST_COLUMN_ID_LIST.reduce(
    (acc, id) => ({ ...acc, [id]: true }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoInvoiceListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const EMSP_BO_INVOICE_LIST_DEFAULT_OPTIONS: EmspBoInvoiceListOptionsModel =
  {
    columnVisibility: EMSP_BO_INVOICE_LIST_COLUMN_VISIBILITY,
    columnOrder: EMSP_BO_INVOICE_LIST_COLUMN_ORDER,
  };

export const EMSP_BO_INVOICE_LIST_COLUMN_LABEL_I18N_KEY: Record<
  string,
  string
> = {
  invoiceNumber: 'delmonicos-shared-sdk.outputs-invoice.invoice-number',
  emspUserId: 'delmonicos-shared-sdk.outputs-emsp-user.id',
  emspUserDid: 'delmonicos-shared-sdk.outputs-emsp-user.did',
  emspUserFirstName: 'delmonicos-shared-sdk.outputs-emsp-user.first-name',
  emspUserLastName: 'delmonicos-shared-sdk.outputs-emsp-user.last-name',
  emspUserEmail: 'delmonicos-shared-sdk.outputs-emsp-user.email',
  type: 'delmonicos-shared-sdk.outputs-emsp-user-invoice-base.type',
  status: 'delmonicos-shared-sdk.outputs-invoice.status',
  totalTTC: 'delmonicos-shared-sdk.outputs-invoice.total-ttc',
  issuedAt: 'delmonicos-shared-sdk.outputs-invoice.issued-at',
};
